import React, { useState, useEffect } from 'react';
import { GetAttendanceRecords } from './apis/get';
import { PostAttendanceLogin, PostAttendanceLogout, PostAttendanceLeave } from './apis/post';
import toast from 'react-hot-toast';

interface Session {
  id: string;
  loginTime: string;
  logoutTime: string;
  sessionTime: string;
}

interface AttendanceRecord {
  id?: string;
  employeeName: string;
  loginTime?: string;
  logoutTime?: string;
  totalTime?: string;
  date: string;
  type: 'attendance' | 'leave';
  leaveReason?: string;
  sessions?: Session[];
}

const EmployeeAttendance = () => {
  const [employeeName, setEmployeeName] = useState('');
  const [loggedInEmployees, setLoggedInEmployees] = useState<{[key: string]: string}>({});
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [employeeNames, setEmployeeNames] = useState<string[]>([]);
  const [isNewEmployee, setIsNewEmployee] = useState(false);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveReason, setLeaveReason] = useState('');
  const [leaveDate, setLeaveDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchAttendanceRecords();
  }, []);

  const fetchAttendanceRecords = async () => {
    try {
      const response = await GetAttendanceRecords();
      if (response.status === 'success') {
        setAttendanceRecords(response.data || []);
        // Extract unique employee names
        const names = Array.from(new Set(response.data?.map((record: AttendanceRecord) => record.employeeName) || [])) as string[];
        setEmployeeNames(names);
      }
    } catch (error) {
      console.error('Failed to fetch attendance records');
    }
  };

  const handleLogin = async () => {
    if (!employeeName.trim()) {
      toast.error('Please enter employee name');
      return;
    }

    setLoading(true);
    try {
      const istTime = new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000)).toISOString();
      const response = await PostAttendanceLogin({
        employeeName: employeeName.trim(),
        loginTime: istTime
      });

      if (response.status === 'success') {
        setLoggedInEmployees(prev => ({...prev, [employeeName.trim()]: new Date().toLocaleString()}));
        setEmployeeName('');
        setIsNewEmployee(false);
        toast.success('Logged in successfully');
        fetchAttendanceRecords();
      }
    } catch (error) {
      toast.error('Failed to log in');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (empName: string) => {
    setLoading(true);
    try {
      const istTime = new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000)).toISOString();
      const response = await PostAttendanceLogout({
        employeeName: empName,
        logoutTime: istTime
      });

      if (response.status === 'success') {
        const updatedEmployees = {...loggedInEmployees};
        delete updatedEmployees[empName];
        setLoggedInEmployees(updatedEmployees);
        toast.success(`${empName} logged out successfully. Total time: ${response.totalTime || 'N/A'}`);
        fetchAttendanceRecords();
      }
    } catch (error) {
      toast.error('Failed to log out');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutOld = async () => {
    setLoading(true);
    try {
      const istTime = new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000)).toISOString();
      const response = await PostAttendanceLogout({
        employeeName: employeeName.trim(),
        logoutTime: istTime
      });

      if (response.status === 'success') {
        const updatedEmployees = {...loggedInEmployees};
        delete updatedEmployees[employeeName.trim()];
        setLoggedInEmployees(updatedEmployees);
        setEmployeeName('');
        toast.success(`Logged out successfully. Total time: ${response.totalTime || 'N/A'}`);
        fetchAttendanceRecords();
      }
    } catch (error) {
      toast.error('Failed to log out');
    } finally {
      setLoading(false);
    }
  };

  const handleLeave = async () => {
    if (!employeeName.trim() || !leaveReason.trim()) {
      toast.error('Please enter employee name and leave reason');
      return;
    }

    setLoading(true);
    try {
      const response = await PostAttendanceLeave({
        employeeName: employeeName.trim(),
        leaveReason: leaveReason.trim(),
        date: leaveDate
      });

      if (response.status === 'success') {
        setEmployeeName('');
        setLeaveReason('');
        setLeaveDate(new Date().toISOString().split('T')[0]);
        setShowLeaveForm(false);
        setIsNewEmployee(false);
        toast.success('Leave recorded successfully');
        fetchAttendanceRecords();
      }
    } catch (error) {
      toast.error('Failed to record leave');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleString();
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start px-6 bg-transparent text-white">
      <div className="w-full max-w-6xl">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-2xl shadow-2xl mb-6">
          <h1 className="text-3xl font-bold text-yellow-400 mb-8 text-center">
            Employee Attendance System
          </h1>

        {/* Attendance/Leave Form */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 mb-6 max-w-md mx-auto">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setShowLeaveForm(false)}
              className={`flex-1 py-2 px-4 rounded text-sm font-semibold transition-colors ${
                !showLeaveForm ? 'bg-yellow-400 text-black' : 'bg-white/10 text-gray-300'
              }`}
            >
              Attendance
            </button>
            <button
              onClick={() => setShowLeaveForm(true)}
              className={`flex-1 py-2 px-4 rounded text-sm font-semibold transition-colors ${
                showLeaveForm ? 'bg-yellow-400 text-black' : 'bg-white/10 text-gray-300'
              }`}
            >
              Leave
            </button>
          </div>

          {!showLeaveForm ? (
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">Employee Name:</label>
                <div className="space-y-2">
                  <select
                    value={isNewEmployee ? 'new' : employeeName}
                    onChange={(e) => {
                      if (e.target.value === 'new') {
                        setIsNewEmployee(true);
                        setEmployeeName('');
                      } else {
                        setIsNewEmployee(false);
                        setEmployeeName(e.target.value);
                      }
                    }}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded text-white"
                    disabled={loading}
                  >
                    <option value="" className="bg-gray-900">Select Employee</option>
                    {employeeNames.map(name => (
                      <option key={name} value={name} className="bg-gray-900">{name}</option>
                    ))}
                    <option value="new" className="bg-gray-900">+ New Employee</option>
                  </select>
                  {isNewEmployee && (
                    <input
                      type="text"
                      value={employeeName}
                      onChange={(e) => setEmployeeName(e.target.value)}
                      placeholder="Enter new employee name"
                      className="w-full p-3 bg-white/10 border border-white/20 rounded text-white placeholder-gray-400"
                      onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                      disabled={loading}
                    />
                  )}
                </div>
              </div>
              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black font-semibold py-3 px-4 rounded transition-colors"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </div>
          ) : null}

          {showLeaveForm && (
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">Employee Name:</label>
                <div className="space-y-2">
                  <select
                    value={isNewEmployee ? 'new' : employeeName}
                    onChange={(e) => {
                      if (e.target.value === 'new') {
                        setIsNewEmployee(true);
                        setEmployeeName('');
                      } else {
                        setIsNewEmployee(false);
                        setEmployeeName(e.target.value);
                      }
                    }}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded text-white"
                    disabled={loading}
                  >
                    <option value="" className="bg-gray-900">Select Employee</option>
                    {employeeNames.map(name => (
                      <option key={name} value={name} className="bg-gray-900">{name}</option>
                    ))}
                    <option value="new" className="bg-gray-900">+ New Employee</option>
                  </select>
                  {isNewEmployee && (
                    <input
                      type="text"
                      value={employeeName}
                      onChange={(e) => setEmployeeName(e.target.value)}
                      placeholder="Enter new employee name"
                      className="w-full p-3 bg-white/10 border border-white/20 rounded text-white placeholder-gray-400"
                      disabled={loading}
                    />
                  )}
                </div>
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Leave Date:</label>
                <input
                  type="date"
                  value={leaveDate}
                  onChange={(e) => setLeaveDate(e.target.value)}
                  className="w-full p-3 bg-white/10 border border-white/20 rounded text-white"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Leave Reason:</label>
                <textarea
                  value={leaveReason}
                  onChange={(e) => setLeaveReason(e.target.value)}
                  placeholder="Enter reason for leave"
                  className="w-full p-3 bg-white/10 border border-white/20 rounded text-white placeholder-gray-400 h-20 resize-none"
                  disabled={loading}
                />
              </div>
              <button
                onClick={handleLeave}
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold py-3 px-4 rounded transition-colors"
              >
                {loading ? 'Recording Leave...' : 'Record Leave'}
              </button>
            </div>
          )}
        </div>

        {/* Currently Logged In Employees */}
        {Object.keys(loggedInEmployees).length > 0 && (
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-semibold text-yellow-300 mb-4">Currently Logged In</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(loggedInEmployees).map(([name, loginTime]) => (
                <div key={name} className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-white">{name}</h3>
                      <p className="text-gray-300 text-sm">Login: {loginTime}</p>
                    </div>
                    <button
                      onClick={() => handleLogout(name)}
                      disabled={loading}
                      className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm px-3 py-1 rounded transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attendance Records Table */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-yellow-300 mb-4">Attendance Records</h2>
          
          {attendanceRecords.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No attendance records found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 px-4 text-yellow-300">Employee Name</th>
                    <th className="text-left py-3 px-4 text-yellow-300">Date</th>
                    <th className="text-left py-3 px-4 text-yellow-300">Type</th>
                    <th className="text-left py-3 px-4 text-yellow-300">Login Time</th>
                    <th className="text-left py-3 px-4 text-yellow-300">Logout Time</th>
                    <th className="text-left py-3 px-4 text-yellow-300">Total Time</th>
                    <th className="text-left py-3 px-4 text-yellow-300">Status/Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceRecords.map((record, index) => (
                    <React.Fragment key={record.id || index}>
                      <tr className="border-b border-white/10 hover:bg-white/5">
                        <td className="py-3 px-4 text-white">{record.employeeName}</td>
                        <td className="py-3 px-4 text-gray-300">{record.date}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            record.type === 'leave' 
                              ? 'bg-orange-500/20 text-orange-300' 
                              : 'bg-blue-500/20 text-blue-300'
                          }`}>
                            {record.type === 'leave' ? 'Leave' : 'Attendance'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          {record.sessions && record.sessions.length > 0 ? (
                            <div className="space-y-1">
                              {record.sessions.map((session) => (
                                <div key={session.id} className="text-xs">{session.loginTime}</div>
                              ))}
                            </div>
                          ) : record.type === 'attendance' && record.loginTime ? formatTime(record.loginTime) : '-'}
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          {record.sessions && record.sessions.length > 0 ? (
                            <div className="space-y-1">
                              {record.sessions.map((session) => (
                                <div key={session.id} className="text-xs">{session.logoutTime}</div>
                              ))}
                            </div>
                          ) : record.type === 'attendance' && record.logoutTime ? formatTime(record.logoutTime) : '-'}
                        </td>
                        <td className="py-3 px-4 text-gray-300 font-semibold">{record.totalTime || '-'}</td>
                        <td className="py-3 px-4">
                          {record.type === 'leave' ? (
                            <span className="text-orange-300 text-sm">{record.leaveReason}</span>
                          ) : record.sessions && record.sessions.length > 0 ? (
                            <div className="space-y-1">
                              {record.sessions.map((session) => (
                                <div key={session.id} className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded">
                                  {session.sessionTime}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              record.logoutTime 
                                ? 'bg-green-500/20 text-green-300' 
                                : 'bg-yellow-500/20 text-yellow-300'
                            }`}>
                              {record.logoutTime ? 'Completed' : 'Active'}
                            </span>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendance;